import { doPrivatePostRequest } from "@/api/requests";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/react";
import { AlertDialog, Avatar, Button, Card, Chip, Popover, ListBox, Label, Skeleton, Spinner, toast } from "@heroui/react";
import {
  Briefcase,
  Building2,
  CalendarClock,
  CalendarPlus,
  ChevronRight,
  Circle,
  Copy,
  Ellipsis,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  UserMinus,
  UserX,
  X,
} from "lucide-react";
import { getUserById as getUserByIdApi, removeMyConnection as removeMyConnectionApi } from "@/api/users";
import { getMyMeetsPastByMember as getMyMeetsPastByMemberApi } from "@/api/meets";
import { formatDate, getPastDateRange } from "@/utils/dates";
import { getUserInitials } from "@/utils";
import CreateMeetsDrawer from "@/components/desktop/drawers/create-meets-drawer";
import DesktopCreateQuickMeetsDrawer from "@/components/desktop/drawers/desktop-create-quick-meet-drawer";

// Placeholder — no tags field on the Clerk user object.

function copyToClipboard(value, label) {
  if (!value) return;
  navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.danger(`Couldn't copy ${label.toLowerCase()}`));
}

const QuickAction = ({ icon: Icon, label, onPress }) => (
  <button type="button" onClick={onPress} className="flex flex-col items-center gap-1.5 grow">
    <div className="size-9 rounded-full border flex items-center justify-center text-accent">
      <Icon size={16} />
    </div>
    <span className="text-xs text-muted">{label}</span>
  </button>
);

const InfoRow = ({ icon: Icon, value, copyLabel }) => (
  <div className="flex items-center justify-between gap-2 py-2">
    <div className="flex items-center gap-2.5 min-w-0">
      <Icon size={14} className="text-muted shrink-0" />
      <span className="text-sm truncate">{value}</span>
    </div>

    {copyLabel && (
      <button type="button" aria-label={`Copy ${copyLabel}`} onClick={() => copyToClipboard(value, copyLabel)}>
        <Copy size={14} className="text-muted shrink-0" />
      </button>
    )}
  </div>
);

const DetailsSkeleton = () => (
  <Card>
    <Card.Header className="flex-row gap-3 items-center">
      <Skeleton className="size-11 rounded-full shrink-0" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-5 w-36 rounded-lg" />
        <Skeleton className="h-4 w-48 rounded-lg" />
      </div>
    </Card.Header>

    <Card.Content className="flex flex-col gap-5">
      <div className="flex items-center gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center gap-1.5 grow">
            <Skeleton className="size-9 rounded-full" />
            <Skeleton className="h-3 w-12 rounded-lg" />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-16 rounded-lg" />
        <Skeleton className="h-3 w-full rounded-lg" />
        <Skeleton className="h-3 w-2/3 rounded-lg" />
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32 rounded-lg" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full rounded-lg" />
        ))}
      </div>
    </Card.Content>
  </Card>
);

const NotFound = () => (
  <Card>
    <Card.Content className="flex flex-col items-center gap-3 py-12 text-center">
      <div className="rounded-full bg-default/60 p-4">
        <UserX className="size-6 text-muted" />
      </div>
      <div>
        <p className="font-semibold">Could not find this connection</p>
        <p className="text-sm text-muted">It may have been removed.</p>
      </div>
    </Card.Content>
  </Card>
);

const NetworkDetailsComp = ({ _connection_id, record, onClose, onRemoveConnection }) => {
  const { session } = useSession();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [pastMeets, setPastMeets] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [showCreateMeets, setShowCreateMeets] = useState(false);
  const [showQuickMeets, setShowQuickMeets] = useState(false);
  const [isRemovingConnection, setIsRemovingConnection] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    if (!_connection_id) return;

    let cancelled = false;

    const load = async () => {
      setUser(null);
      setPastMeets(null);
      setNotFound(false);

      const token = await session.getToken();

      getUserByIdApi(_connection_id, token)
        .then((_resp) => {
          if (cancelled) return;
          if (_resp.data.Result === "OK") {
            setUser(_resp.data.Record);
          } else {
            setNotFound(true);
          }
        })
        .catch((err) => {
          console.log(err);
          if (!cancelled) setNotFound(true);
        });

      getMyMeetsPastByMemberApi(_connection_id, getPastDateRange(3), token)
        .then((_resp) => {
          if (cancelled) return;
          setPastMeets(_resp.data.Result === "OK" ? _resp.data.Records || [] : []);
        })
        .catch((err) => {
          console.log(err);
          if (!cancelled) setPastMeets([]);
        });
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_connection_id]);

  const handleRemoveConnection = async () => {
    setIsRemovingConnection(true);
    const token = await session.getToken();

    removeMyConnectionApi(_connection_id, token)
      .then((_resp) => {
        if (_resp.data.Result === "SUCCESS") {
          toast.success("Connection removed");
          setShowRemoveConfirm(false);
   
          onRemoveConnection?.("success", _connection_id);
        } else {
          toast.danger("Couldn't remove connection", { description: "Please try again." });
        }
      })
      .catch((err) => {
        console.log(err);
        toast.danger("Couldn't remove connection", { description: "Check your connection and try again." });
      })
      .finally(() => {
        setIsRemovingConnection(false);
      });
  };

  if (notFound) return <NotFound />;
  if (!user) return <DetailsSkeleton />;

  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  const email = user.email_addresses?.[0]?.email_address;
  const phone = user.phone_numbers?.[0]?.phone_number;
  const role = user.public_metadata?.role || "Member";

  const asParticipant = {
    id: _connection_id,
    fullname: fullName,
    email_address: email,
  };

  const sortedMeets = pastMeets
    ?.slice()
    .sort((a, b) => b["meeting_date"].localeCompare(a["meeting_date"]))
    .slice(0, 3);

  return (
    <>
      <Card>
        <Card.Header className="flex-row gap-3 items-start relative">
          <Avatar size="lg" color="accent" variant="soft">
            {user.has_image && <Avatar.Image src={user.image_url} alt={fullName} />}
            <Avatar.Fallback className="uppercase">{getUserInitials(fullName)}</Avatar.Fallback>
          </Avatar>

          <div className="flex flex-col min-w-0">
            <span className="text-lg font-bold capitalize">{fullName || "Unnamed contact"}</span>
            {/* Company is stubbed — no such field on the Clerk user object */}
            <span className="text-muted text-sm">{role}</span>
          </div>

          {onClose && (
            <Button isIconOnly variant="ghost" className="absolute end-0" onPress={onClose}>
              <X size={16} />
            </Button>
          )}
        </Card.Header>

        <Card.Content className="flex flex-col gap-5">
          {/* Quick actions */}
          <div className="flex items-center justify-between gap-2">
            <QuickAction
              icon={Mail}
              label="Email"
              onPress={() => email && window.open(`mailto:${email}`, "_blank", "noopener,noreferrer")}
            />
            {/* Schedule opens the full create flow with this person pre-added */}
            <QuickAction icon={CalendarPlus} label="Schedule" onPress={() => setShowCreateMeets(true)} />
            {/* Start Loop spins up a quick meet, which begins immediately */}
            <QuickAction icon={Circle} label="Start Loop" onPress={() => setShowQuickMeets(true)} />

            <Popover>
              <Popover.Trigger>
                <button type="button" className="flex flex-col items-center gap-1.5 grow">
                  <div className="size-9 rounded-full border flex items-center justify-center text-accent">
                    <Ellipsis size={16} />
                  </div>
                  <span className="text-xs text-muted">More</span>
                </button>
              </Popover.Trigger>

              <Popover.Content placement="bottom end">
                <Popover.Dialog className="p-1 min-w-[180px]">
                  <ListBox
                    aria-label="Contact actions"
                    onAction={(key) => {
                      if (key === "copy-email") copyToClipboard(email, "Email");
                      if (key === "remove") setShowRemoveConfirm(true);
                    }}
                  >
                    <ListBox.Item id="copy-email" textValue="Copy email">
                      <Copy size={14} />
                      <Label>Copy email</Label>
                    </ListBox.Item>
                    <ListBox.Item id="remove" textValue="Remove connection" variant="danger">
                      <UserMinus size={14} />
                      <Label>Remove connection</Label>
                    </ListBox.Item>
                  </ListBox>
                </Popover.Dialog>
              </Popover.Content>
            </Popover>
          </div>

          {/* About — stubbed, no bio/about field on the user object */}
          <div className="flex flex-col gap-1.5">
            <span className="font-semibold text-sm">About</span>
            <p className="text-sm text-muted leading-relaxed">No bio added yet.</p>
          </div>

          {/* Contact Information */}
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-sm">Contact Information</span>

            <div className="flex flex-col divide-y">
              {email && <InfoRow icon={Mail} value={email} copyLabel="Email" />}
              {phone && <InfoRow icon={Phone} value={phone} copyLabel="Phone number" />}
              <InfoRow icon={Briefcase} value={role} />
              {/* Company, location and LinkedIn are stubbed — no backing fields */}
              <InfoRow icon={Building2} value="—" />
              <InfoRow icon={MapPin} value="—" />
              <InfoRow icon={Linkedin} value="—" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="font-semibold text-sm">Tags</span>
            <div className="flex flex-wrap gap-2">{(user.connection_tags || []).map(tag => <Chip key={tag}><Chip.Label>{tag}</Chip.Label></Chip>)}</div>
            <Button variant="outline" size="sm" onPress={async () => {
              const value = window.prompt("Tags, separated by commas", (user.connection_tags || []).join(", "));
              if (value === null) return;
              try {
                const { data } = await doPrivatePostRequest({ connection_id: _connection_id, tags: value.split(",").map(t => t.trim()).filter(Boolean) }, "/users/connections/tags", await session.getToken());
                if (data.Result !== "SUCCESS") throw new Error();
                setUser(prev => ({ ...prev, connection_tags: data.Tags })); toast.success("Tags saved");
              } catch { toast.danger("Could not save tags"); }
            }}>Edit tags</Button>
          </div>

          {/* Activity — real, from past meets shared with this member */}
          <div className="flex flex-col gap-2">
            <span className="font-semibold text-sm">Activity</span>

            {pastMeets === null ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-9 w-full rounded-lg" />
                ))}
              </div>
            ) : sortedMeets.length === 0 ? (
              <p className="text-sm text-muted">No shared loops in the last 3 months.</p>
            ) : (
              <div className="flex flex-col">
                {sortedMeets.map((meet) => (
                  <button
                    type="button"
                    key={meet["_id"]}
                    onClick={() => navigate(`/meetings/view/${meet["short_code"]}`)}
                    className="flex items-center justify-between gap-3 w-full text-left py-2"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <CalendarClock size={14} className="text-muted shrink-0" />
                      <span className="text-sm truncate">{meet["title"] || "Untitled Meeting"}</span>
                    </div>
                    <span className="text-xs text-muted shrink-0">{formatDate(meet["meeting_date"])}</span>
                  </button>
                ))}
              </div>
            )}

            {pastMeets !== null && pastMeets.length > 3 && (
              <Button variant="ghost" size="sm" className="text-accent self-start px-0">
                View all activity
                <ChevronRight size={14} />
              </Button>
            )}
          </div>
        </Card.Content>
      </Card>
      
      {/* Remove Connection Alert */}
      <AlertDialog isOpen={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog className="sm:max-w-[400px]">
              <AlertDialog.CloseTrigger isDisabled={isRemovingConnection} />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>Remove this connection?</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                <p>
                  <strong className="text-foreground">{fullName || email || "This person"}</strong> will be removed
                  from your network. Past loops you shared will stay in your history.
                </p>
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button slot="close" variant="tertiary" isDisabled={isRemovingConnection}>
                  Cancel
                </Button>
                <Button variant="danger" isPending={isRemovingConnection} onPress={handleRemoveConnection}>
                  {isRemovingConnection && <Spinner color="white" />}
                  Remove connection
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <CreateMeetsDrawer
        show={showCreateMeets}
        initialValues={{ participants: [asParticipant] }}
        onResp={(action) => {
          if (action === "close") setShowCreateMeets(false);
          if (action === "success") setShowCreateMeets(false);
        }}
      />

      <DesktopCreateQuickMeetsDrawer
        _show={showQuickMeets}
        _setShow={setShowQuickMeets}
        initialValues={{ participants: [asParticipant] }}
      />
    </>
  );
};

export default NetworkDetailsComp;